import React, {
  createContext,
  //useCallback,
  useContext,
  useMemo,
  useState,
  //Fragment,

  // Types
  ElementType,
  //KeyboardEvent as ReactKeyboardEvent,
  //MouseEvent as ReactMouseEvent,
} from 'react'

import { Props } from '../../types'
import { render } from '../../utils/render'
import { useId } from '../../hooks/use-id'
//import { Keys } from '../keyboard'
//import { resolvePropValue } from '../../utils/resolve-prop-value'
//import { isDisabledReactIssue7711 } from '../../utils/bugs'

interface StateDefinition {
  head: HTMLTableSectionElement | null
  header: HTMLTableCellElement | null
  body: HTMLCollection | null
  column: HTMLTableColElement | null
  row: HTMLTableRowElement | null
  footer: HTMLTableSectionElement | null

  setHead(element: HTMLTableSectionElement): void
  setHeader(element: HTMLTableCellElement): void
  setBody(element: HTMLCollection): void
  setColumn(element: HTMLTableColElement): void
  setRow(element: HTMLTableRowElement): void
  setFooter(element: HTMLTableSectionElement): void
}

let TableContext = createContext<StateDefinition | null>(null)
TableContext.displayName = 'TableContext'

function useTableContext(component: string) {
  let context = useContext(TableContext)
  if (context === null) {
    let err = new Error(`<${component} /> is missing a parent <${Table.name} /> component.`)
    if (Error.captureStackTrace) Error.captureStackTrace(err, useTableContext)
    throw err
  }
  return context
}

// ---

let DEFAULT_TABLE_TAG = 'table' as const

export function Table<TTag extends ElementType = typeof DEFAULT_TABLE_TAG>(props: Props<TTag>) {
  let [headElement, setHeadElement] = useState<HTMLTableSectionElement| null>(null)
  let [headerElement, setHeaderElement] = useState<HTMLTableCellElement | null>(null)
  let [bodyElement, setBodyElement] = useState<HTMLCollection | null>(null)
  let [columnElement, setColumnElement] = useState<HTMLTableColElement | null>(null)
  let [rowElement, setRowElement] = useState<HTMLTableRowElement | null>(null)
  let [footerElement, setFooterElement] = useState<HTMLTableSectionElement | null>(null)

  let context = useMemo<StateDefinition>(
    () => ({
      head: headElement,
      header: headerElement,
      body: bodyElement,
      column: columnElement,
      row: rowElement,
      footer: footerElement,
      setHead: setHeadElement,
      setHeader: setHeaderElement,
      setBody: setBodyElement,
      setColumn: setColumnElement,
      setRow: setRowElement,
      setFooter: setFooterElement,
    }),
    [
      headElement, setHeadElement, 
      headerElement, setHeaderElement,
      bodyElement, setBodyElement,
      columnElement, setColumnElement,
      rowElement, setRowElement,
      footerElement, setFooterElement
    ]
  )

  return (
    <TableContext.Provider value={context}>
      {render(props, {}, DEFAULT_TABLE_TAG)}
    </TableContext.Provider>
  )
}


// ---

let DEFAULT_THEAD_TAG = 'thead' as const
interface HeadRenderPropArg {}
type HeadPropsWeControl = 'id' | 'ref'

function Head<TTag extends ElementType = typeof DEFAULT_THEAD_TAG>(
  props: Props<TTag, HeadRenderPropArg, HeadPropsWeControl>
) {
  let state = useTableContext([Table.name, Head.name].join('.'))
  let id = `headlessui-table-head-${useId()}`


  let propsWeControl = { ref: state.setHead, id }
  return render({ ...props, ...propsWeControl }, {}, DEFAULT_THEAD_TAG)
}

// ---

let DEFAULT_THEADER_TAG = 'th' as const
interface HeaderRenderPropArg {}
type HeaderPropsWeControl = 'id' | 'ref'

function Header<TTag extends ElementType = typeof DEFAULT_THEADER_TAG>(
  props: Props<TTag, HeaderRenderPropArg, HeaderPropsWeControl>
) {
  let state = useTableContext([Table.name, Header.name].join('.'))
  let id = `headlessui-table-header-${useId()}`


  let propsWeControl = { ref: state.setHeader, id }
  return render({ ...props, ...propsWeControl }, {}, DEFAULT_THEADER_TAG)
}


// ---

let DEFAULT_TBODY_TAG = 'tbody' as const
interface BodyRenderPropArg {}
type BodyPropsWeControl = 'id' | 'ref'

function Body<TTag extends ElementType = typeof DEFAULT_TBODY_TAG>(
  props: Props<TTag, BodyRenderPropArg, BodyPropsWeControl>
) {
  let state = useTableContext([Table.name, Body.name].join('.'))
  let id = `headlessui-table-body-${useId()}`


  let propsWeControl = { ref: state.setBody, id }
  return render({ ...props, ...propsWeControl }, {}, DEFAULT_TBODY_TAG)
}


// ---

let DEFAULT_COLUMN_TAG = 'td' as const
interface ColumnRenderPropArg {}
type ColumnPropsWeControl = 'id' | 'ref'

function Column<TTag extends ElementType = typeof DEFAULT_COLUMN_TAG>(
  props: Props<TTag, ColumnRenderPropArg, ColumnPropsWeControl>
) {
  let state = useTableContext([Table.name, Column.name].join('.'))
  let id = `headlessui-table-col-${useId()}`


  let propsWeControl = { ref: state.setColumn, id }
  return render({ ...props, ...propsWeControl }, {}, DEFAULT_COLUMN_TAG)
}



// ---

let DEFAULT_ROW_TAG = 'tr' as const
interface RowRenderPropArg {}
type RowPropsWeControl = 'id' | 'ref'

function Row<TTag extends ElementType = typeof DEFAULT_ROW_TAG>(
  props: Props<TTag, RowRenderPropArg, RowPropsWeControl>
) {
  let state = useTableContext([Table.name, Row.name].join('.'))
  let id = `headlessui-table-row-${useId()}`


  let propsWeControl = { ref: state.setRow, id }
  return render({ ...props, ...propsWeControl }, {}, DEFAULT_ROW_TAG)
}


// ---

let DEFAULT_TFOOT_TAG = 'tfoot' as const
interface FooterRenderPropArg {}
type FooterPropsWeControl = 'id' | 'ref'

function Footer<TTag extends ElementType = typeof DEFAULT_TFOOT_TAG>(
  props: Props<TTag, FooterRenderPropArg, FooterPropsWeControl>
) {
  let state = useTableContext([Table.name, Footer.name].join('.'))
  let id = `headlessui-table-footer-${useId()}`


  let propsWeControl = { ref: state.setFooter, id }
  return render({ ...props, ...propsWeControl }, {}, DEFAULT_TFOOT_TAG)
}


Table.Head = Head
Table.Header = Header
Table.Body = Body
Table.Column = Column
Table.Row = Row
Table.Footer = Footer
